import { useEffect } from 'react'
import { PageMetadata } from '@/types/page'

export const metadata: PageMetadata = {
  id: 'customization-3-layout',
  category: 'customization',
  categoryName: 'Customization',
  name: 'Layout & Settings',
  icon: '📐',
  path: '/customization/layout',
  title: 'Zendesk Widget - Layout & General Settings',
  description: 'Configure widget position, content scale, headers, locale, z-index, and screen offsets'
}

export default function Layout() {
  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`
  }, [])

  // Just show the working HTML test page in an iframe - make it seamless
  return (
    <div className="fixed inset-0 md:left-72 top-0 bottom-0">
      <iframe
        src="/layout-settings-test.html"
        className="w-full h-full border-0 block"
        title="Zendesk Widget Layout & Settings"
      />
    </div>
  )
}

function LayoutOld() {
  const { state, updateState } = useWidget()
  const [viewMode, setViewMode] = useState<'configure' | 'code'>('configure')

  const codeRef = useHighlight([
    state.position,
    state.contentScale,
    state.locale,
    state.zIndex,
    state.settings,
    viewMode
  ])

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`
  }, [])

  const updatePosition = (updates: Partial<typeof state.position>) => {
    updateState({
      position: { ...state.position, ...updates }
    })
  }

  const updateOffset = (
    device: 'web' | 'mobile',
    axis: 'horizontal' | 'vertical',
    value: number
  ) => {
    updateState({
      position: {
        ...state.position,
        offset: {
          ...state.position.offset,
          [device]: {
            ...state.position.offset[device],
            [axis]: value
          }
        }
      }
    })
  }

  const updateSettings = (key: keyof typeof state.settings) => {
    updateState({
      settings: {
        ...state.settings,
        [key]: !state.settings[key]
      }
    })
  }

  return (
    <>
      <header>
        <div className="py-6 px-4 sm:px-6 md:px-8 mt-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {metadata.description}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="py-6 px-4 sm:px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
          {/* Left column: Form - Scrollable */}
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-4">
            {/* View Mode Toggle */}
            <section className="mb-4">
              <div className="flex justify-between items-center">
                <nav className="isolate flex divide-x divide-gray-200 rounded-lg bg-white dark:bg-licorice border border-gray-200">
                  <button
                    onClick={() => setViewMode('configure')}
                    className={`px-6 py-2 text-sm font-medium rounded-l-lg ${
                      viewMode === 'configure'
                        ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    Configure
                  </button>
                  <button
                    onClick={() => setViewMode('code')}
                    className={`px-6 py-2 text-sm font-medium rounded-r-lg ${
                      viewMode === 'code'
                        ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    Code
                  </button>
                </nav>
              </div>
            </section>

            {viewMode === 'configure' ? (
              <div className="space-y-6">
                {/* Content Scale */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Content Scale</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Adjust the widget's content scaling as a percentage (50-200%).
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="50"
                      max="200"
                      value={state.contentScale}
                      onChange={(e) => updateState({ contentScale: Number(e.target.value) })}
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="50"
                      max="200"
                      value={state.contentScale}
                      onChange={(e) => updateState({ contentScale: Number(e.target.value) })}
                      className="w-20 px-2 py-1.5 border rounded-md text-sm text-gray-600"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">%</span>
                  </div>
                </section>

                {/* Widget Settings */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Widget Settings</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <input
                        id="hideHeader"
                        type="checkbox"
                        checked={state.settings.hideHeader}
                        onChange={() => updateSettings('hideHeader')}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor="hideHeader" className="text-sm font-medium text-licorice dark:text-white">
                        Hide headers everywhere
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        id="hideNewConversationButton"
                        type="checkbox"
                        checked={state.settings.hideNewConversationButton}
                        onChange={() => updateSettings('hideNewConversationButton')}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor="hideNewConversationButton" className="text-sm font-medium text-licorice dark:text-white">
                        Hide new conversation button
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        id="conversationList"
                        type="checkbox"
                        checked={state.settings.conversationList}
                        disabled={state.settings.hideHeader}
                        onChange={() => updateSettings('conversationList')}
                        className="h-4 w-4 rounded border-gray-300 disabled:opacity-50"
                      />
                      <label htmlFor="conversationList" className="text-sm font-medium text-licorice dark:text-white">
                        Hide header for conversation list
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        id="messageLog"
                        type="checkbox"
                        checked={state.settings.messageLog}
                        disabled={state.settings.hideHeader}
                        onChange={() => updateSettings('messageLog')}
                        className="h-4 w-4 rounded border-gray-300 disabled:opacity-50"
                      />
                      <label htmlFor="messageLog" className="text-sm font-medium text-licorice dark:text-white">
                        Hide headers for conversation view
                      </label>
                    </div>
                  </div>
                </section>

                {/* Widget Side */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Widget Side</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Choose which side of the screen the widget appears on.
                  </p>
                  <nav className="isolate flex divide-x divide-gray-200 rounded-lg bg-white dark:bg-licorice border border-gray-200 w-fit">
                    <button
                      onClick={() => updatePosition({ side: 'left' })}
                      className={`px-6 py-2 text-sm font-medium rounded-l-lg ${
                        state.position.side === 'left'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
                      }`}
                    >
                      Left
                    </button>
                    <button
                      onClick={() => updatePosition({ side: 'right' })}
                      className={`px-6 py-2 text-sm font-medium rounded-r-lg ${
                        state.position.side === 'right'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
                      }`}
                    >
                      Right
                    </button>
                  </nav>
                </section>

                {/* Web Offset */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Web Offset</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Adjust the distance from the screen edges on desktop browsers.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="text-sm text-licorice dark:text-white">
                      Horizontal (px)
                      <br />
                      <input
                        type="number"
                        value={state.position.offset.web.horizontal}
                        onChange={(e) => updateOffset('web', 'horizontal', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                        placeholder="16"
                      />
                    </label>
                    <label className="text-sm text-licorice dark:text-white">
                      Vertical (px)
                      <br />
                      <input
                        type="number"
                        value={state.position.offset.web.vertical}
                        onChange={(e) => updateOffset('web', 'vertical', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                        placeholder="16"
                      />
                    </label>
                  </div>
                </section>

                {/* Mobile Offset */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Mobile Offset</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Adjust the distance from the screen edges on mobile devices.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="text-sm text-licorice dark:text-white">
                      Horizontal (px)
                      <br />
                      <input
                        type="number"
                        value={state.position.offset.mobile.horizontal}
                        onChange={(e) => updateOffset('mobile', 'horizontal', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                        placeholder="16"
                      />
                    </label>
                    <label className="text-sm text-licorice dark:text-white">
                      Vertical (px)
                      <br />
                      <input
                        type="number"
                        value={state.position.offset.mobile.vertical}
                        onChange={(e) => updateOffset('mobile', 'vertical', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                        placeholder="16"
                      />
                    </label>
                  </div>
                </section>

                {/* Locale */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Locale</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Set the language for the widget interface.
                  </p>
                  <label className="text-sm text-licorice dark:text-white">
                    Language
                    <br />
                    <select
                      value={state.locale}
                      onChange={(e) => updateState({ locale: e.target.value })}
                      className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                    >
                      <option value="en-US">English (US)</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="it">Italian</option>
                      <option value="ja">Japanese</option>
                      <option value="pt-BR">Portuguese (BR)</option>
                      <option value="nl">Dutch</option>
                    </select>
                  </label>
                </section>

                {/* Z-Index */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Z-Index</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Set the CSS z-index for all widget iframes.
                  </p>
                  <label className="text-sm text-licorice dark:text-white">
                    Z-Index Value
                    <br />
                    <input
                      type="number"
                      value={state.zIndex}
                      onChange={(e) => updateState({ zIndex: Number(e.target.value) })}
                      className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                      placeholder="999999"
                    />
                  </label>
                </section>
              </div>
            ) : (
              <CodeOutput
                code={generateCode({
                  position: state.position,
                  contentScale: state.contentScale,
                  settings: state.settings,
                  locale: state.locale,
                  zIndex: state.zIndex
                })}
                codeRef={codeRef}
              />
            )}
          </div>

          {/* Right column: Widget Preview - Fixed */}
          <WidgetPreview />
        </div>
      </div>
    </>
  )
}
